import { useCallback, useEffect, useState } from 'react';
import { eventEmitter } from '../../../lib/eventEmmitter';
import {
  requestCameraAccess,
  requestCameraClose,
  requestPhotoCapture,
} from '../lib/sendMessageToNative';
import type { ICameraPhoto } from '../types';

interface IUseNativeCameraReturn {
  photo: ICameraPhoto | null;
  isCapturing: boolean;
  error: string | null;
  requestCamera: () => void;
  capturePhoto: () => void;
  clearPhoto: () => void;
}

export const useNativeCamera = (): IUseNativeCameraReturn => {
  const [photo, setPhoto] = useState<ICameraPhoto | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePhotoCaptured = useCallback((payload: unknown) => {
    let photoData: ICameraPhoto | undefined = undefined;
    try {
      photoData = payload as ICameraPhoto;
      if (!photoData.base64 || !photoData.format) {
        throw new Error(
          `Invalid photo data:${!photoData.base64 ? ' missing base64' : ''} ${
            !photoData.format ? ' missing format' : ''
          }`
        );
      }
      setPhoto(photoData);
      setIsCapturing(false);
      setError(null);
    } catch (err) {
      console.error('Error handling captured photo:', err);
      console.error('Photo data:', photoData);
      setError('Failed to process captured photo');
      setIsCapturing(false);
    }
  }, []);

  const handleCameraError = useCallback((payload: unknown) => {
    try {
      const errorData = payload as { error: string };
      setError(errorData.error);
      setIsCapturing(false);
    } catch (err) {
      console.error('Error handling camera error:', err);
      setError('Unknown camera error');
      setIsCapturing(false);
    }
  }, []);

  useEffect(() => {
    eventEmitter.on('CAMERA_PHOTO_CAPTURED', handlePhotoCaptured);
    eventEmitter.on('CAMERA_ERROR', handleCameraError);

    return () => {
      requestCameraClose();
      eventEmitter.off('CAMERA_PHOTO_CAPTURED', handlePhotoCaptured);
      eventEmitter.off('CAMERA_ERROR', handleCameraError);
    };
  }, [handlePhotoCaptured, handleCameraError]);

  const requestCamera = useCallback(() => {
    setError(null);
    requestCameraAccess();
  }, []);

  const capturePhoto = useCallback(() => {
    setError(null);
    setIsCapturing(true);
    requestPhotoCapture();
  }, []);

  const clearPhoto = useCallback(() => {
    setPhoto(null);
    setError(null);
    setIsCapturing(false);
  }, []);

  return {
    photo,
    isCapturing,
    error,
    requestCamera,
    capturePhoto,
    clearPhoto,
  };
};
